import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { SegmentedControl } from "@msuite/ui/segmented-control";
import type { IncomingInvoiceReviewFormSchema } from "@msuite/utils/schemas";
import { useFormContext, useWatch } from "react-hook-form";

/** Props Interface */
interface ReviewVerdictControlProps {
	/** Accepting waits on the invoice's IBAN being confirmed. */
	isAcceptBlocked: boolean;
}

/** What the control offers: accepting as invoiced, accepting with a Kürzung, or rejecting. */
type ReviewVerdict = "approved" | "reduced" | "rejected";

/**
 * Accept, reduce or reject — the choice every other field of the review follows. Reducing is an
 * acceptance with a Kürzung, so it sets `state` to `approved` and `isReduced` on.
 */
export function ReviewVerdictControl({ isAcceptBlocked }: ReviewVerdictControlProps) {
	/** Context */
	const methods = useFormContext<IncomingInvoiceReviewFormSchema>();

	/** Hooks */
	const { t } = useTranslation();
	const [state, isReduced] = useWatch({ control: methods.control, name: ["state", "isReduced"] });

	/** Functions */
	function handleOnChange(next: ReviewVerdict) {
		methods.setValue("state", next === "rejected" ? "rejected" : "approved", { shouldDirty: true });
		methods.setValue("isReduced", next === "reduced", { shouldDirty: true });
		methods.clearErrors();
	}

	/** Computed */
	const verdict: ReviewVerdict =
		state === "rejected" ? "rejected" : isReduced ? "reduced" : "approved";

	/** Render */
	return (
		<SegmentedControl
			value={verdict}
			onChange={handleOnChange}
			items={[
				{
					label: t("incoming_invoices.decision.accept"),
					value: "approved",
					cyId: "incoming-invoice-review-accept",
					isDisabled: isAcceptBlocked,
					leftIcon: (
						<Icon
							name="Check"
							stroke="var(--color-emerald-500)"
						/>
					),
				},
				{
					label: t("incoming_invoices.decision.reduce"),
					value: "reduced",
					cyId: "incoming-invoice-review-reduce",
					isDisabled: isAcceptBlocked,
					leftIcon: (
						<Icon
							name="Scissors"
							stroke="var(--color-orange-500)"
						/>
					),
				},
				{
					label: t("incoming_invoices.decision.reject"),
					value: "rejected",
					cyId: "incoming-invoice-review-reject",
					leftIcon: (
						<Icon
							name="X"
							stroke="var(--color-rose-500)"
						/>
					),
				},
			]}
		/>
	);
}
